/**
 *  * Get a list of latchkeys with names and display names.
 *
 * @param {ProductBcom} product
 * @returns {Array<{ name: string, displayName: string }>}
 */
function getLatchkeyList(product) {
  const { orderedMasterGroupList, memberDisplayGroupsMap } = product?.detail;

  if (!memberDisplayGroupsMap || !Array.isArray(orderedMasterGroupList)) return [];

  return orderedMasterGroupList
    .map((latchkey) => {
      let displayName = `${latchkey}`;
      const isCaliforniaKing = displayName.trim().toLowerCase() === 'california king';

      if (isCaliforniaKing) {
        displayName = 'cal king';
      }

      return {
        name: latchkey,
        displayName,
      };
    })
    .filter((latchkey) => {
      const memberList = memberDisplayGroupsMap[latchkey.name];

      return Array.isArray(memberList) && memberList.length;
    });
}
/**
 *
 * @param {string} string
 * @returns {string}
 */
const removeBrackets = (string) => (typeof string === 'string' ? string.replace(/\[.*\]/, '').trim() : '');
/**
 *
 * @param {string} label
 * @param {string} defaultLabel
 * @returns {string}
 */
const getFormattedPriceLabel = (label, defaultLabel = '') => removeBrackets(label) || defaultLabel;
/**
 * Get index of the selected latchkey in the provided latchkey list.
 *
 * @param {LatchKeyList} latchkeyList - An array of latchkeys with names and display names.
 * @param {object} data - Data coming from xapi response
 * @param {ProductBcom} data.product
 * @param {UiKillswitchesBcom} data.uiKillSwitches
 * @returns {number}
 */
function getSelectedLatchkey(latchkeyList, data) {
  const selectedLatchkey = (
    data.product?.relationships?.selectedLatchKey || ''
  )
    .trim()
    .toLowerCase();
  const index = latchkeyList.findIndex((latchkey) => {
    const name = latchkey.name.trim().toLowerCase();

    if (
      data.uiKillSwitches?.isConsolidatedPhase3Enabled
            && selectedLatchkey
            && typeof selectedLatchkey === 'string'
    ) {
      return selectedLatchkey === name;
    }

    return name === 'queen';
  });

  return index === -1 ? 0 : index;
}
/**
 * Get latchkey data based on the provided latchkey list and latchkey ID.
 *
 * @param {ProductBcom} product
 * @param {LatchKeyList} latchkeyList - An array of latchkeys with names and display names.
 * @param {number} latchkeyId - The index of the latchkey in the latchkeyList.
 * @returns {Object} - The latchkey data from the latchKeyToProductMap. Returns an empty object if not found.
 */
function getLatchkeyData(product, latchkeyList, latchkeyId) {
  const latchkeyName = latchkeyList[latchkeyId]?.name || '';
  const latchKeyToProductMap = product?.relationships?.latchKeyToProductMap;

  return (latchKeyToProductMap && latchKeyToProductMap[latchkeyName]) || {};
}
/**
 * Get view state latchkey data based on the provided product information, UiKillswitches, and latchkey list.
 *
 * @param { ProductBcom } product
 * @param { UiKillswitchesBcom } uiKillSwitches
 * @param { LatchKeyList } latchkeyList - An array of latchkeys with names and display names.
 * @returns { LatchKeyData }
 */
function getViewStateLatchkeyData(product, uiKillSwitches, latchkeyList) {
  const selectedLatchkey = getSelectedLatchkey(latchkeyList, {
    product,
    uiKillSwitches,
  });

  return {
    latchkeyList,
    selectedLatchkey,
    selectedLatchkeyData: getLatchkeyData(
      product,
      latchkeyList,
      selectedLatchkey,
    ),
    isLatchkeyProduct: latchkeyList.length > 1,
    statesMap: {},
  };
}
/**
 *
 * @param {any[]} array
 * @return {any[]}
 */
function addIdToEachElement(array) {
  return array.map((el, index) => ({
    ...el,
    id: index,
  }));
}
/**
 * Get selected price data.
 *
 * @param {object} selectedPrice
 * @param {object} selectedPrice.price
 * @param {TieredPrice} selectedPrice.price.tieredPrice
 * @param {FinalPrice} selectedPrice.price.finalPrice
 * @param {PriceTypeBcom} selectedPrice.price.priceType - The price type object with explicit types.
 * @param {number} selectedPrice.price.priceTypeId
 * @param {{url?:string}} selectedPrice.price.policy
 * @returns {SelectedPriceData}
 */
function getSelectedPriceData(selectedPrice) {
  const price = selectedPrice?.price || {};

  const {
    tieredPrice = [],
    priceType = {},
    policy = {},
    finalPrice = null,
  } = price;

  const {
    onSale, willBe, isFinalOfferType, text,
  } = priceType;

  const regularPriceTierIndex = tieredPrice.length - 1;
  const regularPriceTier = tieredPrice[regularPriceTierIndex] || {};
  const lowestSalePriceTier = finalPrice || tieredPrice[0] || {};
  const originalPriceTier = tieredPrice[tieredPrice.length - 1] || {};

  const isShowRegularPrice = !!onSale;
  const isRegularPriceStrikeThrough = !willBe;
  const policyUrl = policy.url || '';
  const isFinalOffer = !!text && text.toLowerCase() === 'the final offer';

  return {
    finalPrice,
    priceType,
    originalPriceTier,
    regularPriceTier,
    lowestSalePriceTier,
    percentOff: lowestSalePriceTier?.values?.[0]?.percentOff?.[0] || '',
    policyUrl,
    tieredPrice: addIdToEachElement(tieredPrice),
    flags: {
      isNowPriceLabel: tieredPrice.some((priceTier) => priceTier?.label?.includes('Now')),
      isShowRegularPrice,
      isRegularPriceStrikeThrough,
      isShowPriceTypeText: !!(!isFinalOfferType && text),
      isFinalOffer,
    },
  };
}
/**
 * Get formatted string based on provided pricing object
 *
 * @param {Pricing} pricing
 * @returns string
 */
function getPriceRange(pricing) {
  const tieredPrices = pricing?.price?.tieredPrice || [];
  const lastTieredPrice = tieredPrices[tieredPrices.length - 1];
  const formattedValues = Array.isArray(lastTieredPrice?.values)
    ? lastTieredPrice.values.map((value) => value.formattedValue)
    : [];
  const label = getFormattedPriceLabel(lastTieredPrice?.label);

  return `${label} ${formattedValues.join(' – ')}`.trim();
}

/**
 * Get object with formatted strings for price range and percent off range based on provided pricing object
 *
 * @param {Pricing} pricing
 * @returns string
 */
function getRedesignPriceRange(pricing) {
  const tieredPrices = pricing?.price?.tieredPrice || [];
  const finalPrices = pricing?.price?.finalPrice;
  const lastTieredPrice = finalPrices || tieredPrices[tieredPrices.length - 1];

  const formattedPercentOff = Array.isArray(lastTieredPrice?.values)
    ? lastTieredPrice.values.map((value) => value.percentOff?.[0]).filter(Boolean)
    : [];

  const formattedValues = Array.isArray(lastTieredPrice?.values)
    ? lastTieredPrice.values.map((value) => value.formattedValue)
    : [];

  const label = getFormattedPriceLabel(lastTieredPrice?.label);
  const hidePriceLabel = finalPrices || label.toLowerCase() === 'sale'
   || (Array.isArray(lastTieredPrice?.values) && lastTieredPrice.values.some((value) => value.type === 'regular'));

  return {
    price: `${hidePriceLabel ? '' : `${label} `}${formattedValues.join(' – ')}`.trim(),
    percent: `${formattedPercentOff.join(' – ')}`.trim(),
  };
}

/**
 * Mutate tiered price data by reversing the order and formatting price labels.
 *
 * @param {object} data
 * @param {Pricing} data.pricing
 */
function mutateTieredPrice(data) {
  const tieredPrice = data.pricing?.price?.tieredPrice || [];
  const basicPrices = /orig|reg|will be/i;
  // TODO: because of custom store implementation, logic fires twice independently on bot hclient and server.
  // Temporary guard - store to be replaced with vuex
  if (tieredPrice.length > 1 && basicPrices.test(tieredPrice[0]?.label)) tieredPrice.reverse();
  tieredPrice.forEach((priceData) => {
    if (priceData.label) priceData.label = getFormattedPriceLabel(priceData.label);
  });
}
/**
 * Checks if an offer is a Loyallist offer based on its properties.
 * @param {Badge} offer - The offer to be checked.
 * @returns {boolean} Returns true if the offer is a Loyallist offer, otherwise false.
 */
function isLoyallistOffer(offer, priceSimplificationEnabled = false) {
  return (
    (typeof offer?.colorClassName === 'string'
        && offer?.colorClassName.trim().toLowerCase() === 'loyaltybadge')
        || (priceSimplificationEnabled && (offer?.promotionType === 'Loyalty Points Per Dollar')));
}
/**
 * Get the list of valid offers based on the provided badge data and options.
 * @param {BadgeData} badgeData - The badge data containing information about offers.
 * @param {Object} options - Additional options for filtering offers (default is an empty object).
 * @param {string[]} options.colorwayWhitelist - An array of badge IDs representing the whitelist of colorways.
 * @returns {BadgesList} An array of valid offer objects.
 */
function getOfferList(badgeData, options) {
  if (!badgeData) {
    return [];
  }
  const { colorwayWhitelist, priceSimplificationEnabled } = options;
  const badgesMap = badgeData.badgesMap || {};
  const badgeIds = badgeData.badgeIds || [];
  const validOfferIds = badgeIds.filter((badgeId) => {
    const offerData = badgesMap[badgeId] || {};
    const { promotionType, checkoutDescription } = offerData;
    const isLoyallist = isLoyallistOffer(offerData, priceSimplificationEnabled);

    return (
    // Offer contains the required data
    // offerName data if it is a loyallist offer
      ((isLoyallist && offerData.offerName)
                // description if it is a non loyallist offer
                || (!isLoyallist && offerData.description))
            // If colorwayWhitelist exists, array is a whitelist for valid ids
            && (offerData.applicableToAllUpcs
                || !Array.isArray(colorwayWhitelist)
                || colorwayWhitelist.includes(badgeId))
            // checkoutDescription doesn't contain the word fake
            && !(
              typeof checkoutDescription === 'string'
                && checkoutDescription.toLowerCase().indexOf('fake') > -1
            )
            // Not a free shipping promoType
            && !(
              typeof promotionType === 'string'
                && promotionType.trim().toLowerCase() === 'free shipping'
            )
    );
  });
  return validOfferIds.map((offerId) => badgesMap[offerId]);
}
/**
 * Update the offer list by adding additional properties to each offer.
 * @param {BadgesList} offerList - The list of offers to be updated.
 * @returns {void}
 */
function updateOfferList(offerList, options = {}) {
  offerList.forEach((offer) => {
    Object.assign(offer, {
      isLoyallistOffer: isLoyallistOffer(offer, options.priceSimplificationEnabled),
      isGWP:
                typeof offer.promotionType === 'string'
                && offer.promotionType.trim().toLowerCase() === 'bundled gwp',
    });
  });
}
/**
 * Get offers data based on badge data and additional data.
 * @param {BadgeData} badgeData - The badge data.
 * @param {Object} additionalData - Additional data including colorway information.
 * @param {boolean} additionalData.isColorwayOffers - Indicates if colorway offers are enabled.
 * @param {boolean} additionalData.isConsolidatedMaster - Indicates type of the page.
 * @param {boolean} [additionalData.isUpcSelected] - Indicates if a UPC is selected.
 * @param {number} [additionalData.selectedColorId] - The selected color ID.
 * @param {string} [additionalData.selectedColorName] - The name of the selected color.
 * @param {string} [additionalData.priceSimplificationEnabled] - Pricing simplification flag.
 * @param {Traits} [additionalData.traits] - Additional traits including color information.
 * @returns {{
 *  isMultipleOffers: boolean,
 *  offerList: Badge[],
 *  topOffer: Badge
 * }} An object containing information about offers.
 */
function getOffersData(badgeData, additionalData) {
  const { isColorwayOffers, isConsolidatedMaster, priceSimplificationEnabled } = additionalData;
  const options = { priceSimplificationEnabled };

  if (
    isColorwayOffers
        && (!isConsolidatedMaster || additionalData?.isUpcSelected)
  ) {
    let selectedColorId = additionalData?.selectedColorId;

    if (isConsolidatedMaster) {
      const colorMap = additionalData.traits?.colors?.colorMap || {};
      const selectedColorData = Object.values(colorMap).find(
        (colorData) => colorData.name === additionalData.selectedColorName,
      );

      selectedColorId = selectedColorData?.id;
    }

    options.colorwayWhitelist = (additionalData.traits?.colors?.colorMap?.[selectedColorId]
                && additionalData.traits?.colors?.colorMap?.[selectedColorId]
                  ?.pricing?.badgeIds)
            || [];
  }

  const offerList = getOfferList(badgeData, options);
  updateOfferList(offerList, options);

  return {
    isMultipleOffers: offerList.length > 1,
    offerList,
    topOffer: offerList[0] || {},
  };
}

function _filterOffersByColor(offers, colorBadgeIds) {
  if (!Array.isArray(offers) || !Array.isArray(colorBadgeIds)) {
    return offers;
  }

  return offers.filter(
    (offer) => offer.applicableToAllUpcs || colorBadgeIds.includes(offer.promoId),
  );
}
function _isApplicableWithOffer(offers, finalPrice) {
  const finalPriceOfferIds = finalPrice.applicablePromotions;

  if (!Array.isArray(offers) || !Array.isArray(finalPriceOfferIds)) {
    return false;
  }

  const offerIds = offers.map((offer) => offer.promoId);

  return (
    offerIds.length >= finalPriceOfferIds.length
        && offerIds.some((offerId) => finalPriceOfferIds.includes(offerId))
  );
}
/**
 *
 * @param {*} param0
 * @returns {{isWithOffer:boolean}}
 */

function getWithOffer({
  offerList, finalPrice, colorBadges, flags,
}) {
  // We dont need withOffer(finalPrice) object here
  // We are taking finalPrice value from lowestPriceTier property
  // And if its not applicable when isFinalPriceValidCheckEnabled is on
  // it will not show because its behind the isWithOffer flag
  let filteredOffersByColor = offerList;

  const { isColorwayOffers } = flags;

  if (isColorwayOffers && colorBadges.length) {
    filteredOffersByColor = _filterOffersByColor(offerList, colorBadges);
  }

  const isWithOffer = finalPrice
        && !finalPrice.maskPromotion
        && !!filteredOffersByColor.length
        && _isApplicableWithOffer(filteredOffersByColor, finalPrice);

  return {
    isWithOffer,
  };
}

/**
 * @param {Object} params
 * @param {Badge []} params.offerList
 * @param {Badge} params.topOffer
 * @returns {Badge[]}
 */
const getPerksPromotions = ({ offerList, topOffer }) => {
  const offers = topOffer.isGWP ? offerList.filter((offer) => !offer.isGWP) : offerList?.slice(1);
  return offers || [];
};

/**
 * @param {Object} params
 * @param {Badge []} params.offerList
 * @returns {{offers: Badge[], isMultipleOffers: boolean}}
 */
const getFinalPriceAdditionalOffers = ({ offerList = [] }) => {
  const offers = offerList?.slice(1);
  const isMultipleOffers = offers?.length > 1;
  return {
    offers,
    isMultipleOffers,
  };
};

export {
  getLatchkeyList,
  removeBrackets,
  getFormattedPriceLabel,
  getSelectedLatchkey,
  getLatchkeyData,
  getViewStateLatchkeyData,
  getSelectedPriceData,
  getPriceRange,
  getRedesignPriceRange,
  mutateTieredPrice,
  getOffersData,
  getOfferList,
  getWithOffer,
  getPerksPromotions,
  getFinalPriceAdditionalOffers,
};
